import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
  EntityId,
  EntityState,
  PayloadAction,
} from "@reduxjs/toolkit";
import { API } from "common/utils";
import { createSelector } from "reselect";
import { FileEntry } from "./FileEntry";
import { RootState } from "./rootReducer";

export enum FILE_UPLOAD_TYPE {
  FERTILISER_RECEIPT = "FERTILISER_RECEIPT",
  FERTILISER_BAG = "FERTILISER_BAG",
  SEED_MIXES_RECEIPT = "SEED_MIXES_RECEIPT",
  SEED_MIXES_BAG = "SEED_MIXES_BAG",
  FIELD_IMAGES = "FIELD_IMAGES",
  SOIL_TEST_REPORT = "SOIL_TEST_REPORT",
  WATER_TEST_REPORT = "WATER_TEST_REPORT",
  MILK_RECORDING_REPORT = "MILK_RECORDING_REPORT",
  CARCASS_REPORT = "CARCASS_REPORT",
  CARBON_AUDIT_REPORT = "CARBON_AUDIT_REPORT",
  SOIL_ENVIRONMENT_REPORT = "SOIL_ENVIRONMENT_REPORT",
  DETAILED_NUTRIENT_MANAGEMENT_PLAN = "DETAILED_NUTRIENT_MANAGEMENT_PLAN",
  BIODIVERSITY_ACTION_PLAN = "BIODIVERSITY_ACTION_PLAN",
  BIODIVERSITY_HABITAT_MAP = "BIODIVERSITY_HABITAT_MAP",
  SFI_ADVICE_REPORT = "SFI_ADVICE_REPORT",
  BIODIVERSITY_SUMMARY_FLOWER = "BIODIVERSITY_SUMMARY_FLOWER",
}

export type CreateFileEntryPayload = {
  fileDescription: string;
  fileUploadId: string;
  associatedUserId: string;
  fileUploadType: FILE_UPLOAD_TYPE;
  userId: string;
};

export const FILE_ENTRY_REJECTION_PERIOD_DAYS = 30;

class FileEntryAPI extends API<FileEntry> {
  async getAllFileEntriesForUser(
    associatedUserId?: string
  ): Promise<FileEntry[]> {
    const query = associatedUserId
      ? `?associatedUserId=${associatedUserId}`
      : "";
    const res = await fetch(`${this.baseUrl}${this.entity}${query}`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt") || ""}`,
        "Content-Type": "application/json",
      },
    });
    const fileEntries = (await res.json()) as FileEntry[];

    return fileEntries;
  }

  async createFileEntry(
    payload: CreateFileEntryPayload
  ): Promise<{ guid: string }> {
    const res = await fetch(`${this.baseUrl}${this.entity}`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt") || ""}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        fileDescription: payload.fileDescription,
        fileUploadId: payload.fileUploadId,
        associatedUserId: payload.associatedUserId,
        fileUploadType: payload.fileUploadType,
      }),
    });

    if (!res.ok) {
      throw new Error(`HTTP error! Status: ${res.status}`);
    }

    return (await res.json()) as { guid: string };
  }
}

const fileEntryApi = new FileEntryAPI("fileEntry");

export const deleteFileEntry = createAsyncThunk<Promise<number>, FileEntry>(
  "fileEntry/delete",
  async (fileEntry: FileEntry, { dispatch }) => {
    const res = await fileEntryApi.deleteOne(fileEntry.guid);

    if (res.ok) {
      dispatch(fileEntrySlice.actions.deleteFileEntry(fileEntry.guid));
    }
    return res.status;
  }
);

export const getSingleFileEntry = createAsyncThunk<Promise<FileEntry>, string>(
  "fileEntry/getSingle",
  async (guid: string, { dispatch }) => {
    const res = await fileEntryApi.getOne(guid);
    return res;
  }
);

export const createFileEntry = createAsyncThunk<
  FileEntry,
  CreateFileEntryPayload
>("fileEntry/create", async (payload: CreateFileEntryPayload, { dispatch }) => {
  const response = await fileEntryApi.createFileEntry(payload);
  const created = await dispatch(getSingleFileEntry(response.guid));
  return created.payload as FileEntry;
});

export const fileEntryAdapter = createEntityAdapter<FileEntry>({
  // we need this because IDs are stored in a field other than `field.id`
  selectId: (fileEntry: FileEntry) => fileEntry.guid,
  // Keeps the array sorted by guid
  sortComparer: (a: FileEntry, b: FileEntry) => a.guid.localeCompare(b.guid),
});

export const {
  selectAll: selectAllFileEntries,
  selectById: selectFileEntryById,
} = fileEntryAdapter.getSelectors((state: RootState) => state.fileEntries);

type Status = "success" | "error" | null;
type LoadingState = "idle" | "pending";
type FileEntryState = EntityState<FileEntry> & {
  loading: LoadingState;
  error: null;
  status: Status;
};

const initialFileEntryState: FileEntryState = fileEntryAdapter.getInitialState({
  loading: "idle",
  error: null,
  status: null,
});

export const getAllFileEntriesForUser = createAsyncThunk<
  Promise<Array<FileEntry>>,
  { associatedUserId?: string }
>(
  "fileEntries/getFileEntries",
  async ({ associatedUserId }: { associatedUserId?: string }, { dispatch }) => {
    const fileEntries: FileEntry[] =
      await fileEntryApi.getAllFileEntriesForUser(associatedUserId);
    return fileEntries;
  }
);

export const updateFileEntry = createAsyncThunk(
  "fileEntry/update",
  async (fileEntry: FileEntry, { dispatch }) => {
    const { userId, ...payload } = fileEntry;
    await fileEntryApi.updateOne(payload);
    const result = await dispatch(getSingleFileEntry(fileEntry.guid));
    return result;
  }
);

const selectAssociatedUserId = (state: RootState, associatedUserId: string) =>
  associatedUserId;

export const selectFileEntriesForUser = createSelector(
  [selectAllFileEntries, selectAssociatedUserId],
  (fileEntries: FileEntry[], associatedUserId: string) => {
    const fileEntriesForUser = fileEntries.filter(
      (r: FileEntry) => r.associatedUserId === associatedUserId
    );
    return fileEntriesForUser.sort((a: FileEntry, b: FileEntry) => {
      return (
        Date.parse(b.creationDate ?? "") - Date.parse(a.creationDate ?? "")
      );
    });
  }
);

const fileEntrySlice = createSlice({
  name: "fileEntries",
  initialState: initialFileEntryState,
  reducers: {
    resetFileEntries: () => initialFileEntryState,
    deleteFileEntry: (state, action: PayloadAction<string>) =>
      fileEntryAdapter.removeOne(state, action.payload),
  },
  extraReducers: {
    [getAllFileEntriesForUser.pending.type]: (state, action) => {
      state.loading = "pending";
      state.status = null;
    },
    [getAllFileEntriesForUser.fulfilled.type]: (state, action) => {
      fileEntryAdapter.setAll(
        state,
        action.payload as readonly FileEntry[] | Record<EntityId, FileEntry>
      );
      state.loading = "idle";
      state.status = "success";
    },
    [updateFileEntry.pending.type]: (state, action) => {
      state.loading = "pending";
      state.status = null;
    },
    [updateFileEntry.fulfilled.type]: (
      state,
      action: PayloadAction<FileEntry>
    ) => {
      state.loading = "idle";
      state.status = "success";
      fileEntryAdapter.upsertOne(state, action.payload as unknown as FileEntry);
    },
    [updateFileEntry.rejected.type]: (state, action) => {
      state.loading = "idle";
      state.status = "error";
      state.error = action.error;
    },
    [createFileEntry.fulfilled.type]: (
      state,
      action: PayloadAction<FileEntry>
    ) => {
      state.loading = "idle";
      state.status = "success";
      fileEntryAdapter.addOne(state, action.payload as unknown as FileEntry);
    },
  },
});

export const { resetFileEntries } = fileEntrySlice.actions;

export const fileEntriesReducer = fileEntrySlice.reducer;
